import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getCostDetail, updateCost, deleteCost } from "../../api/contract/cost";
import TableCell from "../../components/common/ui/TableCell";
import SidePanel from "../../components/common/layout/SidePanel";
import DocSummaryCard from "../../components/common/form/DocSummary";
import NivoBar from "../../components/common/graphs/NivoBar";
import AttachmentBox from "../../components/common/papers/AttachmentBox";
import FileList from "../../components/common/papers/FileList";
import { softDeleteDoc } from "../../api/papers";

const Cost_view = ({ costId: propCostId, onDeleted, onUpdated, onClose }) => {
  const { costId: routeCostId } = useParams();
  const navigate = useNavigate();

  const costId = propCostId || routeCostId;

  const [cost, setCost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [showVersionPanel, setShowVersionPanel] = useState(false);

  const [loadedFiles, setLoadedFiles] = useState([]); // 기존 파일 관리
  const attachmentRef = useRef(null); //파일관련 변수

  // 필드 라벨 매핑
  const fieldLabels = {
    code: "코드",
    construction_type: "공종",
    payment_date: "지불 날짜",
    expected_amount: "계획 기성 금액",
    actual_amount: "실제 기성 금액",
    memo: "메모",
  };

  /** 상세 가져오기 */
  const fetchDetail = async () => {
    if (!costId) return;
    setLoading(true);
    setErrMsg("");

    try {
      const data = await getCostDetail(costId);

      setCost(data);
      setForm({
        ...data.meta,
      });
    } catch (error) {
      console.error("비용 상세 불러오기 실패:", error);
      setErrMsg("비용 정보를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const fetchDetailById = async (id) => {
    setLoading(true);
    try {
      const data = await getCostDetail(id);
      setCost(data);
      setForm({
        ...data.meta,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, [costId]);

  /** 폼 상태 변경 */
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  /** 저장 */
  const handleSave = async () => {
    try {
      const formData = new FormData();

      formData.append("code", form.code ?? "");
      formData.append("construction_type", form.construction_type ?? "");
      formData.append("payment_date", form.payment_date ?? "");
      formData.append("expected_amount", form.expected_amount ?? 0);
      formData.append("actual_amount", form.actual_amount ?? 0);
      formData.append("memo", form.memo ?? "");
      formData.append("version", cost.meta.version);

      const formJson = JSON.stringify(Object.fromEntries(formData.entries()));

      const updated = await updateCost(costId, { meta: formJson });
      try {
        await attachmentRef.current?.save(updated.dns);
      } catch (e) {
        console.warn("attachment 저장 에러", e);
      }
      await fetchDetailById(updated.dns);
      onUpdated?.(updated.dns);

      alert("수정되었습니다");
      setEditing(false);
    } catch (err) {
      console.error("수정 실패:", err);
      const message =
        err.response?.data?.detail || "수정 실패 다시 시도해주세요";
      alert(`수정 실패: ${message}`);
    }
  };

  /** 삭제 */
  const handleDelete = async () => {
    if (
      !window.confirm(
        "해당 비용과 모든 버전 문서가 삭제됩니다. 정말 삭제하시겠습니까?"
      )
    )
      return;

    try {
      // 1) 현재 DNS
      const dnsList = [cost.dns];

      // 2) 이전 버전들의 DNS 모두 추가
      if (cost.versions?.length) {
        dnsList.push(...cost.versions.map((v) => v.dns));
      }

      // 3) 일괄 삭제
      await Promise.all(dnsList.map((dns) => softDeleteDoc(dns, true)));

      alert("삭제 완료");

      if (onDeleted) onDeleted();
      else navigate(0);
    } catch (err) {
      console.error("삭제 실패:", err);
      alert("삭제 실패");
    }
  };

  if (!costId) return <div className="p-6 text-center">비용 ID 없음</div>;
  if (loading) return <div className="p-6 text-center">로딩 중...</div>;
  if (errMsg)
    return <div className="p-6 text-center text-red-500">{errMsg}</div>;
  if (!cost)
    return <div className="p-6 text-center">비용 정보를 찾을 수 없습니다.</div>;

  const meta = cost.meta;

  const formatAmount = (val) => {
    if (val === null || val === undefined) return "-";

    // 숫자로만 구성된 문자열인지 체크
    if (typeof val === "number" || /^[0-9]+$/.test(val)) {
      const num = Number(val);
      return isNaN(num) ? "-" : `${num.toLocaleString("ko-KR")} 원`;
    }

    // 숫자가 아니라면 그냥 문자열로 출력
    return String(val);
  };

  return (
    <div className="max-w-8xl mx-auto p-6 space-y-6">
      {/* 버전 버튼 */}
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-3">
        <button
          onClick={() => setShowVersionPanel(true)}
          className="px-4 py-2 rounded-full text-sm bg-blue-100 text-blue-700 hover:bg-blue-200"
        >
          v{meta.version} {meta.is_current ? "(최신)" : "(이전)"}
        </button>

        <SidePanel
          isOpen={showVersionPanel}
          onClose={() => setShowVersionPanel(false)}
          title="수정 내역"
          width={1000}
          side="left"
        >
          <div className="overflow-y-auto max-h-[80vh]">
            <table className="min-w-full text-sm border border-gray-200 rounded-lg">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="px-4 py-2 border">Ver</th>
                  <th className="px-4 py-2 border">코드</th>
                  <th className="px-4 py-2 border">수정 날짜</th>
                  <th className="px-4 py-2 border">변경 사항</th>
                </tr>
              </thead>

              <tbody>
                {cost.versions?.map((v) => (
                  <tr
                    key={v.dns}
                    className="border-t hover:bg-blue-50 cursor-pointer"
                    onClick={() => {
                      fetchDetailById(v.dns);
                      setShowVersionPanel(false);
                      setEditing(false);
                    }}
                  >
                    <td className="px-4 py-2 border text-center">
                      <span className="px-2 py-1 bg-gray-100 rounded-full text-xs">
                        v{v.version}
                      </span>
                    </td>
                    <td className="px-4 py-2 border">{v.code}</td>
                    <td className="px-4 py-2 border text-gray-600">
                      {v.created_at
                        ? new Date(v.created_at).toLocaleString("ko-KR")
                        : "-"}
                    </td>
                    <td className="px-4 py-2 border">
                      {v.changes?.length ? (
                        <ul className="list-disc pl-4 space-y-1">
                          {v.changes.map((c, i) => (
                            <li key={i}>
                              <b>{fieldLabels[c.field] || c.field}</b>:
                              {formatAmount(c.old)} → {formatAmount(c.new)}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        "변경 없음"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SidePanel>

        {/* 액션 버튼 */}
        <div className="flex gap-2 flex-wrap">
          {meta.is_current && (
            <>
              {editing ? (
                <>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                  >
                    저장
                  </button>
                  <button
                    onClick={() => {
                      setEditing(false);
                      fetchDetail();
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-900 rounded-md hover:bg-gray-300"
                  >
                    취소
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setEditing(true)}
                  className="btn-edit"
                >
                  수정
                </button>
              )}

              <button
                onClick={handleDelete}
                className="btn-delete"
              >
                삭제
              </button>

              {onClose && (
                <button
                  onClick={onClose}
                  className="btn-cancel"
                >
                  닫기
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* 본문 */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* 왼쪽 테이블 */}
        <div className="flex-1 space-y-4">
          <div className="overflow-x-auto ">
            <table className="min-w-full text-sm">
              <tbody >
                <tr >
                  <TableCell
                    label="코드"
                    editing={editing}
                    value={meta.code}
                    input={
                      <input
                        type="text"
                        name="code"
                        value={form.code || ""}
                        onChange={handleChange}
                        className="w-full border rounded-md px-2 py-1"
                      />
                    }
                  />

                  <TableCell
                    label="공종"
                    editing={editing}
                    value={meta.construction_type}
                    input={
                      <select
                        name="construction_type"
                        value={form.construction_type || ""}
                        onChange={handleChange}
                        className="w-full border rounded-md px-2 py-1"
                      >
                        <option value="본공사">본공사</option>
                        <option value="전기공사">전기공사</option>
                        <option value="통신공사">통신공사</option>
                        <option value="소방공사">소방공사</option>
                      </select>
                    }
                  />
                </tr>

                <tr >
                  <TableCell
                    label="지불 날짜"
                    editing={editing}
                    value={meta.payment_date}
                    input={
                      <input
                        type="date"
                        name="payment_date"
                        value={form.payment_date || ""}
                        onChange={handleChange}
                        className="w-full border rounded-md px-2 py-1"
                      />
                    }
                  />
                  <TableCell
                    label="작성자"
                    editing={false}
                    value={cost.created_by}
                  />
                </tr>

                <tr >
                  <TableCell
                    label="계획 금액"
                    editing={editing}
                    value={formatAmount(meta.expected_amount)}
                    input={
                      <input
                        type="number"
                        name="expected_amount"
                        value={form.expected_amount || ""}
                        onChange={handleChange}
                        className="w-full border rounded-md px-2 py-1 text-right"
                      />
                    }
                  />

                  <TableCell
                    label="실제 금액"
                    editing={editing}
                    value={formatAmount(meta.actual_amount)}
                    input={
                      <input
                        type="number"
                        name="actual_amount"
                        value={form.actual_amount || ""}
                        onChange={handleChange}
                        className="w-full border rounded-md px-2 py-1 text-right"
                      />
                    }
                  />
                </tr>

                <tr className="align-top">
                  <TableCell
                    label="메모"
                    editing={editing}
                    colSpan={3}
                    value={
                      <div className="w-full max-w-[800px] min-h-[160px] rounded-md px-2 py-2 whitespace-pre-wrap">
                        {meta.memo || "-"}
                      </div>
                    }
                    input={
                      <textarea
                        name="memo"
                        value={form.memo || ""}
                        onChange={handleChange}
                        className="w-full max-w-[800px] min-h-[160px] rounded-md border px-2 py-2 resize-none"
                      />
                    }
                  />
                </tr>

                <tr className="align-top">
                  <TableCell
                    label="첨부파일"
                    colSpan={3}
                    editing={editing}
                    value={
                      <FileList
                        dns={cost.dns}
                        onFilesLoaded={(files) => {
                          const mapped = files.map((f) => ({
                            _type: "library",
                            id: f.file_id ?? f.id,
                            name: f.name,
                          }));
                          setLoadedFiles(mapped);
                        }}
                      />
                    }
                    input={
                      <AttachmentBox
                        ref={attachmentRef}
                        initial={loadedFiles}
                      />
                    }
                  />
                </tr>

              </tbody>
            </table>
          </div>
        </div>

        {/* 오른쪽 요약 */}
        <div className="w-full lg:w-1/3 space-y-4">
          {/* 그래프 */}
          <div className="border bg-white shadow-sm pb-9">
            <NivoBar
              title="계획 vs 실제"
              data={[
                {
                  id: "비용",
                  계획: meta.expected_amount ?? 0,
                  실제: meta.actual_amount ?? 0,
                },
              ]}
              keys={["계획", "실제"]}
              indexBy="id"
              height={250}
              groupMode="grouped"
              colors={({ id }) => (id === "계획" ? "#3B82F6" : "#F59E0B")}
            />
          </div>

          {/* 요약 카드 */}
          <DocSummaryCard
            className="bg-white"
            title={`v${meta.version} 비용 요약`}
            blocks={[
              {
                label: "계획 대비 차이",
                content: (() => {
                  const diff =
                    (meta.expected_amount ?? 0) - (meta.actual_amount ?? 0);
                  const percent = meta.expected_amount
                    ? ((diff / meta.expected_amount) * 100).toFixed(1)
                    : "-";
                  return (
                    <span
                      style={{
                        color: diff > 0 ? "red" : diff < 0 ? "blue" : "black",
                      }}
                    >
                      {formatAmount(diff)} ({percent}%)
                    </span>
                  );
                })(),
              },
              {
                label: "지불 날짜",
                content: meta.payment_date
                  ? new Date(meta.payment_date).toLocaleDateString("ko-KR")
                  : "-",
              },
            ]}
            sections={[
              {
                label: "집행률(%)",
                value: (() => {
                  if (meta.expected_amount && meta.actual_amount) {
                    const rate = (
                      (meta.actual_amount / meta.expected_amount) *
                      100
                    ).toFixed(1);
                    return (
                      <span
                        style={{
                          color:
                            rate < 100 ? "blue" : rate > 100 ? "red" : "black",
                        }}
                      >
                        {rate} %
                      </span>
                    );
                  }
                  return "-";
                })(),
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default Cost_view;
